/**
 * Authentication service for handling login, logout, and token storage
 */
import axios from 'axios';

// API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://your-api-gateway-url.execute-api.us-east-1.amazonaws.com/dev';

// Local storage keys
const TOKEN_KEY = 'tradingview_app_token';
const USER_KEY = 'tradingview_app_user';

/**
 * Set auth token in axios headers
 * @param {string} token - JWT token
 */
const setAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

/**
 * Initialize auth - set token from storage if it exists
 */
export const initAuth = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    setAuthToken(token);
    return true;
  }
  return false;
};

/**
 * Login user
 * @param {string} username - Username
 * @param {string} password - Password
 * @returns {Promise<Object>} Auth result with token and user info
 */
export const login = async (username, password) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/login`, {
      username,
      password
     }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    const { token, user } = response.data;
    
    // Save to local storage
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    
    // Set auth token in axios headers
    setAuthToken(token);
    
    return { token, user };
  } catch (error) {
    throw error.response?.data?.error || 'Login failed';
  }
};

/**
 * Verify token is valid
 * @returns {Promise<boolean>} True if token is valid
 */
export const verifyToken = async () => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      return false;
    }
    
    const response = await axios.post(`${API_BASE_URL}/auth/verify`, {}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data.valid;
  } catch (error) {
    // Clear invalid token
    logout();
    return false;
  }
};

/**
 * Logout user
 */
export const logout = () => {
  // Remove from local storage
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  
  // Remove from axios headers
  setAuthToken(null);
};

/**
 * Get current user
 * @returns {Object|null} User info or null if not logged in
 */
export const getCurrentUser = () => {
  const user = localStorage.getItem(USER_KEY);
  return user ? JSON.parse(user) : null;
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if authenticated
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem(TOKEN_KEY);
};

export default {
  login,
  logout,
  verifyToken,
  getCurrentUser,
  isAuthenticated,
  initAuth
};
